import { atom, useSetAtom, useStore } from 'jotai'
import type { Getter, Setter } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { useEffect } from 'react'
import { ClientError } from '~/lib/error/clientError'
import { type SerializedError, eventNavigate$ } from '~/navigation'
import { RecoverableClientError } from './lib/recoverableClientError'
import { RedirectError } from './lib/redirectError'
import { scope } from './scope'

export const error$ = scope.atom<Error | undefined>(undefined, 'error$')

// error state の navigation context (sessionStorage) に serialize して保存する atom
const serializedError$ = atom<SerializedError | undefined>((get): SerializedError | undefined => {
  const error = get(error$)
  if (error === undefined) {
    return undefined
  }

  if (error instanceof ClientError) {
    if (error.content.type === 'string') {
      return {
        type: 'ClientError',
        content: { type: error.content.type, message: error.content.message },
      }
    }
    if (error.content.type === 'html') {
      return {
        type: 'ClientError',
        content: { type: error.content.type, fragments: error.content.fragments },
      }
    }
    if (error.content.type === 'memberRelation') {
      return {
        type: 'ClientError',
        content: { type: error.content.type, errorType: error.content.errorType },
      }
    }
  } else if (error instanceof RedirectError) {
    if (error.target.type === 'YAHOO_REAUTH_YID') {
      return {
        type: 'RedirectError',
        content: {
          type: error.target.type,
          url: error.target.url,
        },
      }
    }
    if (error.target.type === 'MEMBER_OAUTH_YID') {
      return {
        type: 'RedirectError',
        content: {
          type: error.target.type,
        },
      }
    }
  } else if (error instanceof RecoverableClientError) {
    if (error.content.type === 'string') {
      return {
        type: 'RecoverableClientError',
        content: { type: error.content.type, message: error.content.message },
      }
    }
    if (error.content.type === 'html') {
      return {
        type: 'RecoverableClientError',
        content: { type: error.content.type, fragments: error.content.fragments },
      }
    }
  }

  return {
    type: 'UnknownError',
    message: error.message,
  }
})

// ScopedProvider で store のインスタンスが　scope を優先するインスタンスに書き変わってしまうため
// 予約入力内の store を作成する
// https://ikyu.atlassian.net/wiki/spaces/RESUIUX/pages/6929547300/ADR-041
export const store$ = scope.atom<ReturnType<typeof useStore> | undefined>(undefined)
export function useInitScopedStore() {
  const store = useStore()
  const set = useSetAtom(store$)
  useEffect(() => {
    set(store)
  }, [set, store])
}

export function atomWithError<Value>(read: (get: Getter) => Value) {
  return atom((get) => {
    try {
      return read(get)
    } catch (e) {
      const error = e instanceof Error ? e : new Error('Unknown error has occurred', { cause: e })
      const store = get(store$)
      if (store !== undefined) {
        store.set(error$, error)
      }
    }
  })
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  const { createStore } = await import('jotai')
  describe('atomWithError', () => {
    it('returns expected atom value', () => {
      const store = createStore()
      // @ts-ignore
      store.set(store$, store)
      const value$ = atom('test value')
      const sut$ = atomWithError((get) => {
        return get(value$)
      })
      expect(store.get(sut$)).toBe('test value')
      expect(store.get(error$)).toBe(undefined)
    })
    it('sets error atom on error', () => {
      const store = createStore()
      // @ts-ignore
      store.set(store$, store)
      const error = new Error('test error')
      const sut$ = atomWithError(() => {
        throw error
      })
      expect(store.get(sut$)).toBe(undefined)
      expect(store.get(error$)).toBe(error)
    })
  })
}

type CleanupFn = () => void
type GetterWithPeek = Getter & {
  peek: Getter
}
type SetterWithRecurse = Setter & {
  recurse: Setter
}
export function atomEffectWithError(
  effectFn: (get: GetterWithPeek, set: SetterWithRecurse) => void | CleanupFn
) {
  return atomEffect((get, set) => {
    try {
      return effectFn(get, set)
    } catch (e) {
      const error = e instanceof Error ? e : new Error('Unknown error has occurred', { cause: e })
      set(error$, error)
    }
  })
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  const { createStore } = await import('jotai')
  async function nextTick() {
    return new Promise((resolve) => setTimeout(resolve, 0))
  }
  describe('atomEffectWithError', () => {
    it('subscribe to atom changes', async () => {
      const store = createStore()
      const value$ = atom<string | undefined>(undefined)
      const result$ = atom<string | undefined>(undefined)
      const sut$ = atomEffectWithError((get, set) => {
        const value = get(value$)
        set(result$, value)
      })
      expect(store.get(result$)).toBe(undefined)
      store.set(value$, 'test value')
      store.sub(sut$, () => void 0)
      await nextTick()
      expect(store.get(result$)).toBe('test value')
    })
  })
  it('subscribe to error throws', async () => {
    const store = createStore()
    const error = new Error('test error')
    const sut$ = atomEffectWithError((get, set) => {
      throw error
    })
    expect(store.get(error$)).toBe(undefined)
    store.sub(sut$, () => void 0)
    await nextTick()
    expect(store.get(error$)).toBe(error)
  })
}

// error$ は予約処理でエラーが発生した場合セットされ、この atomEffect が発火し navigation で遷移する
// エラーふすまでブラウザバックすると error$ に値が入ったまま予約入力に戻るが、
// error$ は予約処理でしか更新しないため atomEffect は発火しない
export const listenReserveError$ = atomEffect((get, set) => {
  const error = get(serializedError$)
  if (error === undefined) {
    return
  }
  set(eventNavigate$, { type: 'reserveFailed', error })
})
